/* eslint-disable @typescript-eslint/no-explicit-any -- DIRECT-239681 */

import { AbortError, createError, isErrorType, serializeError } from '@adv-frontend/error';
import {
    AsyncThunk,
    AsyncThunkAction,
    AsyncThunkOptions,
    AsyncThunkPayloadCreator,
    createAsyncThunk,
    GetDispatch,
    GetState,
} from '@reduxjs/toolkit';

import { JSONValue, toSerializableObject } from '@direct-frontend/stdlib';
import type { AsyncThunkConfig, GetStoreFn, ModuleStore } from './types';

/**
 * Добавляет к типу дополнительные поля из AsyncThunk'а.
 *
 * @typeParam T – Базовый тип.
 * @typeParam CreatedAsyncThunk – AsyncThunk из которого нужно достать дополнительные поля.
 */
type WithAdditionalActions<T, CreatedAsyncThunk, Returned, ThunkArg, ThunkApiConfig extends AsyncThunkConfig> =
    CreatedAsyncThunk extends AsyncThunk<Returned, ThunkArg, ThunkApiConfig>
        ? T & {
              pending: CreatedAsyncThunk['pending'];
              fulfilled: CreatedAsyncThunk['fulfilled'];
              rejected: CreatedAsyncThunk['rejected'];
              typePrefix: CreatedAsyncThunk['typePrefix'];
          }
        : never;

/**
 * Возвращает тип AsyncThunkAction для AsyncThunk'а привязанного к стору.
 *
 * @typeParam Returned – Returned тип AsyncThunk'а.
 * @typeParam ThunkArg – ThunkArg тип AsyncThunk'а.
 * @typeParam ThunkApiConfig – ThunkApiConfig тип AsyncThunk'а.
 */
export type BoundAsyncThunkReturnType<Returned, ThunkArg, ThunkApiConfig extends AsyncThunkConfig> = ReturnType<
    AsyncThunkAction<Returned, ThunkArg, ThunkApiConfig>
>;

/**
 * Тип привязанного к стору Async Thunk'а.
 *
 * @typeParam Returned – Returned тип AsyncThunk'а.
 * @typeParam ThunkArg – ThunkArg тип AsyncThunk'а.
 * @typeParam ThunkApiConfig – ThunkApiConfig тип AsyncThunk'а.
 */
export type BoundAsyncThunk<Returned, ThunkArg, ThunkApiConfig extends AsyncThunkConfig> = WithAdditionalActions<
    (
        thunkArg: ThunkArg,
        options?: { signal?: AbortSignal }
    ) => BoundAsyncThunkReturnType<Returned, ThunkArg, ThunkApiConfig>,
    AsyncThunk<Returned, ThunkArg, ThunkApiConfig>,
    Returned,
    ThunkArg,
    ThunkApiConfig
>;

/**
 * Привязывает Async Thunk к стору
 *
 * @param getStore - Функция, возвращающая стор, к которому будет привязан thunk
 * @param thunk - Async Thunk, который будет привязан к стору
 * @returns Обернутый Async Thunk, который можно вызывать без `store.dispatch()`
 */
function bindAsyncThunk<Returned, ThunkArg, ThunkApiConfig extends AsyncThunkConfig>(
    getStore: () => ModuleStore,
    thunk: AsyncThunk<Returned, ThunkArg, ThunkApiConfig>
) {
    function boundAsyncThunk(thunkArg: ThunkArg, options?: { signal?: AbortSignal }) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- автоматический игнор в рамках DIRECT-265451. Исправьте при следующем редактировании при помощи type-assert, чтобы избежать возможных ложно-положительных проверок typecheck!
        // @ts-expect-error TS2554 - thunk из @reduxjs/toolkit принимает только один аргумент. При этом часть типов не экспортируется, поэтому сложно описать типы корректно
        const returnValue: AsyncThunkAction<any, any, any> = thunk(thunkArg, options);

        return getStore().dispatch(returnValue);
    }

    boundAsyncThunk.pending = thunk.pending;
    boundAsyncThunk.fulfilled = thunk.fulfilled;
    boundAsyncThunk.rejected = thunk.rejected;

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
    return boundAsyncThunk as BoundAsyncThunk<Returned, ThunkArg, ThunkApiConfig>;
}

/**
 * Создает реализацию unwrap, которая реджектится модульной ошибкой, если экшен реджектнулся
 */
function buildThunkUnwrap<Returned, ThunkArg, ThunkApiConfig extends AsyncThunkConfig>(
    thunkPromise: ReturnType<AsyncThunkAction<Returned, ThunkArg, ThunkApiConfig>>
) {
    return () =>
        thunkPromise.then(res => {
            if ('meta' in res && 'rejectedWithValue' in res.meta && res.meta.rejectedWithValue) {
                const err = res.payload;

                if (isErrorType(err, Error)) {
                    throw new ThunkError('Thunk rejected with rejectedWithValue', {
                        cause: err,
                        additional: {},
                    });
                }

                throw new ThunkError('Thunk rejected with rejectedWithValue', {
                    cause: undefined,
                    additional: {
                        error: toSerializableObject(err),
                    },
                });
            }

            if ('error' in res && res.error) {
                const err = res.error;

                if (isErrorType(err, Error)) {
                    throw new UnhandledThunkError('Thunk rejected with error', {
                        cause: err,
                        additional: {},
                    });
                }
                // Внутри RTK Abort Error не наследуются от Error, являются просто объектами
                if (err.name === 'AbortError') {
                    throw new AbortError('Thunk action was aborted', {
                        cause: undefined,
                        additional: {
                            message: err.message,
                        },
                    });
                }

                throw new UnhandledThunkError('Thunk rejected with error', {
                    cause: undefined,
                    additional: {
                        error: toSerializableObject(err),
                    },
                });
            }

            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- в остальных случаях выкидываем ошибку
            return res.payload as Returned;
        });
}

/**
 * Необработанная ошибка в thunk
 */
export const UnhandledThunkError = createError<{ error?: JSONValue }>('UnhandledThunkError');
/**
 * Ошибка из метода rejectWithValue
 */
export const ThunkError = createError<{ error?: JSONValue }>('ThunkError');

// Параметры для `buildCreateAsyncThunk()`.
type BuildCreateAsyncThunkOptions = {
    moduleName: string;
    getStore: GetStoreFn;
};

/**
 * Функция для создания thunk в модульном сторе.
 * @internal
 */
export type BuiltCreateAsyncThunk = <
    Returned,
    ThunkArg = void,
    ThunkApiConfig extends AsyncThunkConfig = {
        rejectValue: true | object;
    },
>(
    typePrefix: string,
    payloadCreator: AsyncThunkPayloadCreator<Returned, ThunkArg, ThunkApiConfig>,
    options?: AsyncThunkOptions<ThunkArg, ThunkApiConfig>
) => BoundAsyncThunk<Returned, ThunkArg, ThunkApiConfig>;

/**
 * Создает модульный метод `createAsyncThunk()`.
 *
 * Модульный `createAsyncThunk()`:
 *  1. Создает Async Thunk
 *  2. Привязывает его к модульному стору
 *
 * @internal
 */
export function buildCreateAsyncThunk({ moduleName, getStore }: BuildCreateAsyncThunkOptions): BuiltCreateAsyncThunk {
    return <
        Returned,
        ThunkArg = void,
        ThunkApiConfig extends AsyncThunkConfig = {
            rejectValue: true | object;
        },
    >(
        typePrefix: string,
        payloadCreator: AsyncThunkPayloadCreator<Returned, ThunkArg, ThunkApiConfig>,
        options?: AsyncThunkOptions<ThunkArg, ThunkApiConfig>
    ): BoundAsyncThunk<Returned, ThunkArg, ThunkApiConfig> => {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
        const serializeErrorOptions = {
            serializeError,
        } as AsyncThunkOptions<ThunkArg, ThunkApiConfig>;

        const thunk = createAsyncThunk<Returned, ThunkArg, ThunkApiConfig>(
            `${moduleName}/${typePrefix}`,
            payloadCreator,
            options ? { ...options, serializeError: options.serializeError ?? serializeError } : serializeErrorOptions
        );

        const patchedThunk = (arg: ThunkArg, options?: { signal?: AbortSignal }) => {
            type GetExtra<ThunkApiConfig> = ThunkApiConfig extends {
                extra: infer Extra;
            }
                ? Extra
                : unknown;

            const action = (
                dispatch: GetDispatch<ThunkApiConfig>,
                getState: () => GetState<ThunkApiConfig>,
                extra: GetExtra<ThunkApiConfig>
            ) => {
                const thunkPromise = thunk(arg)(dispatch, getState, extra);

                options?.signal?.addEventListener('abort', () => {
                    thunkPromise.abort(options?.signal?.reason);
                });

                thunkPromise.unwrap = buildThunkUnwrap<Returned, ThunkArg, ThunkApiConfig>(thunkPromise);

                return thunkPromise;
            };

            return action;
        };

        patchedThunk.fulfilled = thunk.fulfilled;
        patchedThunk.rejected = thunk.rejected;
        patchedThunk.pending = thunk.pending;

        return bindAsyncThunk<Returned, ThunkArg, ThunkApiConfig>(
            getStore,
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- поведение проверяется тестами
            patchedThunk as unknown as AsyncThunk<Returned, ThunkArg, ThunkApiConfig>
        );
    };
}
